import { isNonNil, path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { ApplicationType, type applicationTypes } from './applications'
import { type ApplicationScopedPathParams, applicationScopedPathSchema } from './shared'

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

const addUnits =
  (unit: string) =>
  <T>(field: T): T & { unit: string } => {
    return { unit, ...field }
  }

/**
 *  adds _discriminator property to allow discriminated union
 *  typescript (and consequently zod) isn't smart enough to recognize nested
 *  discriminators like d.type.key, so we map that to d._discriminator
 */
const addDiscriminator = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((d) => {
    if (typeof d === 'object' && isNonNil(d)) {
      return {
        ...d,
        _discriminator: (d as { type?: { key?: string } }).type?.key ?? 'DiscriminatorNotFound'
      }
    }
    return { data: d, _discriminator: 'DiscriminatorNotFound' }
  }, schema)

/* -------------------------------------------------------------------------- */
/*                              CORE METRIC TYPES                             */
/* -------------------------------------------------------------------------- */

const labeledStringSchema = z.object({
  label: z.string(),
  value: z.string()
})

const labeledNumberSchema = z.object({
  label: z.string(),
  value: z.number()
})

export const metricSchema = labeledNumberSchema.extend({
  unit: z.string().optional()
})

const optionalMetricSchema = metricSchema.optional()

/* -------------------------------------------------------------------------- */
/*                      SHARED SCHEMA AMONG APPLICATIONS                      */
/* -------------------------------------------------------------------------- */

const baseSystemSchema = <T extends z.infer<typeof applicationTypes>>(key: T) =>
  z.object({
    pass_name: z.string().nullable().optional(),
    type: z.object({
      key: z.literal(key),
      name: z.string()
    }),
    _discriminator: z.literal(key)
  })

/* -------------------------------------------------------------------------- */
/*                               PRODUCT INPUTS                               */
/* -------------------------------------------------------------------------- */

const inputSchema = z.object({
  acres: metricSchema,
  name: labeledStringSchema
})

/**
 *
 * A hybrid is a genetic variety of seed, which can be tracked to get a sense of how different varieties perform.
 *
 * @group Domain Concepts
 *
 * @props lot_number - an identifier for tracking the origin of the seed
 * @props seeds - the number of seeds of this variety that were planted
 * @props population - the average number of seeds per acre that were planted
 * @props seeds_per_unit - the number of seeds that make up one 'unit' of this hybrid
 * @props units - a unit is a bulk measure of seed, this is the number of units of this hybrid that were planted
 * units are generally more meaningful to growers than individual seed counts
 */
const hybridSchema = inputSchema.extend({
  lot_number: labeledStringSchema,
  seeds: labeledNumberSchema,
  seeds_per_unit: labeledNumberSchema.optional(),
  population: labeledNumberSchema,
  units: labeledNumberSchema.transform(addUnits('units')).optional()
})

const liquidInputSchema = inputSchema.extend({
  volume: metricSchema
})

const granularInputSchema = inputSchema.extend({
  mass: metricSchema
})

/* -------------------------------------------------------------------------- */
/*                               APPLICATIONS                                 */
/* -------------------------------------------------------------------------- */

// GLOBAL

const generalSummarySchema = baseSystemSchema(ApplicationType.Global).extend({
  acres: metricSchema.transform((field) => ({ ...field, label: 'Coverage' })),
  average_speed: metricSchema.optional(),
  pass_count: z.number(),
  seconds_applying: z.number().optional(),
  time_finished: z.coerce.date(),
  time_started: z.coerce.date()
})

// IMPLEMENT

const soilSensingDataSchema = z.object({
  organic_matter: metricSchema,
  furrow_temperature: metricSchema,
  clean_furrow: metricSchema,
  furrow_moisture: metricSchema,
  uniform_furrow: metricSchema,
  cec: labeledNumberSchema.transform(addUnits(''))
})

const visionDataSchema = z.object({
  weed_density: optionalMetricSchema,
  crop_density: optionalMetricSchema,
  crop_height: optionalMetricSchema
})

const rideQualityDataSchema = z.object({
  ride_quality: optionalMetricSchema
})

const implementDataSchema = z.union([
  rideQualityDataSchema.extend(visionDataSchema.shape).merge(soilSensingDataSchema),
  rideQualityDataSchema.merge(visionDataSchema) // the doubling is necessary to ensure all soil sensing data is either included or not
])

const vehicleDataSchema = z.record(z.string(), metricSchema).optional()

const implementSummarySchema = baseSystemSchema(ApplicationType.Implement).extend({
  name: z.string(),
  row_average: z.array(implementDataSchema),
  implement_average: implementDataSchema,
  vehicle: vehicleDataSchema
})

// SEEDING

/**
 * @group Domain Concepts
 *
 * @props doubles - the percent of seeds that got doubled up (i.e., two or more seeds in the same spot)
 * @props skips - the percent of seeds that were skipped (i.e., no seed in a spot)
 * @props singulation - the percent of seeds that were singulated (i.e., one seed in a spot)
 *
 * @props population - the average number of seeds per acre
 * @props population_deviation - todo
 * @props population_set_point - todo
 * @props sri - todo
 * @props vacuum - todo
 */
const seedingDataSchema = z.object({
  doubles: optionalMetricSchema,
  population: optionalMetricSchema,
  population_deviation: optionalMetricSchema,
  population_set_point: optionalMetricSchema,
  singulation: optionalMetricSchema,
  skips: optionalMetricSchema,
  sri: optionalMetricSchema,
  vacuum: optionalMetricSchema
})

const seedingSummarySchema = baseSystemSchema(ApplicationType.Seeding).extend({
  acres: metricSchema,
  implement_average: seedingDataSchema,
  row_average: z.array(seedingDataSchema.or(z.object({}))),
  units: labeledNumberSchema.transform(addUnits('units')).optional(),
  pounds: optionalMetricSchema,
  seeds: labeledNumberSchema,
  hybrids: z.array(hybridSchema),
  name: z.string()
})

// FORCE

const forceDataSchema = z.object({
  average_force: optionalMetricSchema,
  ground_contact_percent: optionalMetricSchema,
  margin_force: optionalMetricSchema,
  minimum_force: optionalMetricSchema
})

const forceSummarySchema = baseSystemSchema(ApplicationType.Force).extend({
  name: z.string(),
  implement_average: forceDataSchema,
  row_average: z.array(forceDataSchema.or(z.object({})))
})

// LIQUID

const liquidDataSchema = z.object({
  acres: metricSchema,
  volume: metricSchema,
  /**
   * Missing when no product is applied (divide by zero error)
   */
  rate: metricSchema.optional()
})

const liquidSummarySchema = baseSystemSchema(ApplicationType.Liquid)
  .merge(liquidDataSchema)
  .extend({
    name: z.string(),
    savings: metricSchema.optional(),
    row_total: z.array(liquidDataSchema.or(z.object({}))),
    liquid_inputs: z.array(liquidInputSchema),
    granular_inputs: z.array(granularInputSchema)
  })

// GRANULAR

const granularDataSchema = z.object({
  acres: metricSchema,
  mass: metricSchema,
  /**
   * Missing when no product is applied (divide by zero error)
   */
  rate: metricSchema.optional()
})

const granularSummarySchema = baseSystemSchema(ApplicationType.Granular)
  .merge(granularDataSchema)
  .extend({
    name: z.string(),
    row_average: z.array(z.record(z.string(), metricSchema)),
    row_total: z.array(z.record(z.string(), metricSchema)),
    implement_average: z.record(z.string(), metricSchema),
    granular_inputs: z.array(granularInputSchema)
  })

// PRESSURE

const pressureDataSchema = z.object({
  pressure: metricSchema.optional()
})

const pressureSummarySchema = baseSystemSchema(ApplicationType.Pressure)
  .merge(pressureDataSchema)
  .extend({
    name: z.string(),
    row_average: z.array(pressureDataSchema),
    implement_average: pressureDataSchema
  })

// POSITION

const positionDataSchema = z.object({
  linear_position: metricSchema.optional()
})

const positionSummarySchema = baseSystemSchema(ApplicationType.LinearPosition).extend({
  name: z.string(),
  row_average: z.array(positionDataSchema),
  implement_average: positionDataSchema
})

// HARVEST

const nullableMetricSchema = metricSchema.extend({
  value: z.number().nullable()
})

export const harvestSummarySchema = baseSystemSchema(ApplicationType.Harvest).extend({
  name: z.string(),
  acres: metricSchema,
  dryyieldavg: nullableMetricSchema,
  dryyield: metricSchema,
  moisture: metricSchema.optional(),
  wetweight: metricSchema,
  wetweightavg: nullableMetricSchema,
  dryweight: metricSchema,
  dryweightavg: nullableMetricSchema,
  load: metricSchema.optional()
})

// UNION

export const summaryDataSchema = addDiscriminator(
  z.discriminatedUnion('_discriminator', [
    granularSummarySchema,
    liquidSummarySchema,
    seedingSummarySchema,
    generalSummarySchema,
    implementSummarySchema,
    forceSummarySchema,
    pressureSummarySchema,
    positionSummarySchema,
    harvestSummarySchema
  ])
)

interface PassPathParams extends ApplicationScopedPathParams {
  next?: number
  limit?: number
}

export const summaryDataApi = defineApi({
  pathSchema: applicationScopedPathSchema,
  querySchema: z.object({
    lastRecordId: z.number().optional()
  }),
  pathFor: (p) => {
    return path(`${p.requestType}/streams/${p.orgCode}/${p.streamId}/summary/${p.applicationId}`, {
      lastRecordId: p.lastRecordId
    })
  },
  name: 'GetApplicationSummary',
  description:
    'Get aggregated summary statistics for an application including averages, totals, and per-row metrics',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: summaryDataSchema
})

export const summaryPassSchema = z.object({
  items: z.array(summaryDataSchema).min(1, 'At least one pass required'),
  next: z.number().optional(),
  limit: z.number()
})

export const summaryPassApi = defineApi({
  pathSchema: applicationScopedPathSchema,
  querySchema: z.object({
    next: z.number().optional(),
    limit: z.number().optional()
  }),
  pathFor: (p: PassPathParams) =>
    path(`${p.requestType}/streams/${p.orgCode}/${p.streamId}/summary/${p.applicationId}/pass`, {
      limit: p.limit,
      start: p.next
    }),
  name: 'GetApplicationSummaryByPass',
  description: 'Get paginated summary data broken down by individual passes through the field',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: summaryPassSchema
})

/* -------------------------------------------------------------------------- */
/*                               EXPORTED TYPES                               */
/* -------------------------------------------------------------------------- */

export type SummaryMetric = z.infer<typeof metricSchema>
export type LabeledNumericValue = z.infer<typeof labeledNumberSchema>
export type LabeledStringValue = z.infer<typeof labeledStringSchema>

/**
 * Union of summary data types, covering all application types.
 *
 * Summary data is agronomic data averaged across time for a given application (usually the whole stream,
 * but can be a subset called a "pass", i.e., one segment of the field that was covered in a single pass
 * of the implement without swathing off).
 *
 * To narrow down to a specific application type, use the type guards also provided in this file.
 * e.g., `isSeedingSummaryData(data) && data.seeds`.
 *
 * @group Routes
 *
 * @route
 * `GET /{stitch_prefix}/streams/{stream_id}/{application_id}/summary`
 */
export type SummaryData = z.infer<typeof summaryDataSchema>
/**
 * Seeding is a type of application that tracks individual, "singulated" seeds.
 * Its primary unit of measure is seeds/acre
 */
export type SeedingSummaryData = z.infer<typeof seedingSummarySchema>
/**
 * Liquid is a type of application that tracks volume of liquid product applied (e.g., liquid fertilizer).
 * Its primary unit of measure is gallons/acre
 */
export type LiquidSummaryData = z.infer<typeof liquidSummarySchema>
/**
 * Granular is a type of application that tracks mass of granular product applied (e.g., granular fertilizer).
 * Its primary unit of measure is pounds/acre
 */
export type GranularSummaryData = z.infer<typeof granularSummarySchema>
/**
 * Force is a type of application that tracks the force applied by/to the implement.
 * Its primary unit of measure is pounds.
 * It includes both Down Force and Closing systems
 */
export type ForceSummaryData = z.infer<typeof forceSummarySchema>
/**
 * Global is a catch-all type of application that really, under the hood,
 * combines all summary data that doesn't belong to an application into a single pseudo-application.
 * It includes vehicle speed, total acres covered, etc.
 */
export type GlobalSummaryData = z.infer<typeof generalSummarySchema>
/**
 * Implement is also a bit of a catch-all, but it really is a true "application" as far as the 20|20 is concerned.
 * Often, it includes things that are used to control other applications, such as how SmartFirmer can be used
 * to control Depth systems.
 */
export type ImplementSummaryData = z.infer<typeof implementSummarySchema>
/**
 * Pressure is a type of application that tracks the pressure applied.
 * Its primary unit of measure is PSI.
 * Examples include Row Cleaner
 */
export type PressureSummaryData = z.infer<typeof pressureSummarySchema>
/**
 * Position is a type of application that tracks some measure of distance/position.
 * Its primary unit of measure is inches.
 * Examples include Depth and eventually Boom Height (on Sprayer)
 */
export type PositionSummaryData = z.infer<typeof positionSummarySchema>
/**
 * Harvest is a type of application that tracks the yield of the crop.
 * Its primary unit of measure is bushels/acre.
 */
export type HarvestSummaryData = z.infer<typeof harvestSummarySchema>

/**
 * A hybrid is a variety of seed, which can be tracked to get a sense of how different varieties perform.
 */
export type Hybrid = z.infer<typeof hybridSchema>
/**
 * A liquid input is a specific liquid chemical or fertilizer that is applied.
 */
export type LiquidInput = z.infer<typeof liquidInputSchema>
/**
 * A granular input is a specific granular chemical or fertilizer that is applied.
 * A granular input may be dissolved in a liquid application, so not all granular
 * inputs belong to liquid systems.
 */
export type GranularInput = z.infer<typeof granularInputSchema>

/* -------------------------------------------------------------------------- */
/*                                 TYPE GUARDS                                */
/* -------------------------------------------------------------------------- */

export const isSeedingSummaryData = (data: SummaryData): data is SeedingSummaryData => {
  return data._discriminator === ApplicationType.Seeding
}

export const isForceSummaryData = (data: SummaryData): data is ForceSummaryData => {
  return data._discriminator === ApplicationType.Force
}

export const isLiquidSummaryData = (data: SummaryData): data is LiquidSummaryData => {
  return data._discriminator === ApplicationType.Liquid
}

export const isGranularSummaryData = (data: SummaryData): data is GranularSummaryData => {
  return data._discriminator === ApplicationType.Granular
}

export const isGlobalSummaryData = (data: SummaryData): data is GlobalSummaryData => {
  return data._discriminator === ApplicationType.Global
}

export const isImplementSummaryData = (data: SummaryData): data is ImplementSummaryData => {
  return data._discriminator === ApplicationType.Implement
}

export const isPressureSummaryData = (data: SummaryData): data is PressureSummaryData => {
  return data._discriminator === ApplicationType.Pressure
}

export const isPositionSummaryData = (data: SummaryData): data is PositionSummaryData => {
  return data._discriminator === ApplicationType.LinearPosition
}

export const isHarvestSummaryData = (data: SummaryData): data is HarvestSummaryData => {
  return data._discriminator === ApplicationType.Harvest
}

/* -------------------------------------------------------------------------- */
/*                                  DATA KEYS                                 */
/* -------------------------------------------------------------------------- */

// Zod enums, listing the keys on row-by-row or product-by-product metrics for each application
// these can be used to construct derivative types, such as records of keys with numbers

export const forceDataKeys = forceDataSchema.keyof()
export const pressureDataKeys = pressureDataSchema.keyof()
export const positionDataKeys = positionDataSchema.keyof()
export const seedingDataKeys = seedingDataSchema.keyof()
export const liquidDataKeys = liquidDataSchema.keyof()
export const granularDataKeys = granularDataSchema.keyof()
// the odd format of the implementDataKeys is because it's a union of two objects, unlike the other system types
export const implementDataKeys = implementDataSchema.options[0]
  .keyof()
  .or(implementDataSchema.options[1].keyof())

export const hybridDataKeys = hybridSchema.keyof()
export const liquidInputKeys = liquidInputSchema.keyof()
export const granularInputKeys = granularDataSchema.keyof()
